import { Box, Stack, Tooltip, useColorScheme } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useControllerPowerStateIcon from '../../../hooks/ui/useControllerPowerStateIcon';
import { RootState } from '../../../redux/Store';
import { RobotActions } from '../../../utils/constants';
import WirelessIcon from '../../Icons/Connection/WirelessIcon';
import WiredIcon from '../../Icons/Connection/WiredIcon';

const { CONNECTED } = RobotActions.STATE;

const ControllerConnectionIcon = () => {
  const { t } = useTranslation();
  const { controllerConnected } = useSelector((state: RootState) => state.application);
  const { connectionType } = useSelector((state: RootState) => state.controllerData);
  const { robotState } = useSelector((state: RootState) => state.robot);
  const { mode, systemMode } = useColorScheme();
  const colorMode = mode === 'system' ? systemMode : mode;

  const controllerPowerIcon = useControllerPowerStateIcon(colorMode === 'dark' ? 'light' : 'dark');

  const getConnectionTypeLabel = (type: number | null): string => {
    if (type === 1) return t('connectionDrawer.usb', 'Wired Connection');
    if (type === 2) return t('connectionDrawer.bluetooth', 'Wireless Connection');
    return t('connectionDrawer.unknown', 'Unknown');
  };

  const controllerConnectionType = getConnectionTypeLabel(connectionType);

  if (robotState !== CONNECTED) {
    return null;
  }

  const buttonContent = controllerPowerIcon ? (
    controllerConnected ? (
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap>
        {controllerPowerIcon}
        {connectionType === 1 ? <WiredIcon sx={{ fontSize: 24 }} /> : connectionType === 2 ? <WirelessIcon sx={{ fontSize: 24 }} /> : null}
      </Stack>
    ) : (
      controllerPowerIcon
    )
  ) : null;

  const tooltipTitle = controllerConnected ? (
    t('controllerConnectionIcon.tooltip-connected', 'Controller connected via {{connectionType}}', {
      connectionType: controllerConnectionType,
    })
  ) : (
    <Trans i18nKey="controllerConnectionIcon.tooltip-disconnected">Controller is not connected to robot</Trans>
  );

  return (
    <Tooltip title={tooltipTitle}>
      <Box
        component="span"
        aria-label={t('controllerConnectionIcon.ariaLabel', 'Controller connection status')}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          color: 'text.primary',
        }}
      >
        {buttonContent}
      </Box>
    </Tooltip>
  );
};

export default ControllerConnectionIcon;
